// TrackedItemRow primitive: "View Details" popup modal.
//
// Universal across every consumer. Surfaces the full description,
// required-evidence prose, the escalation-path block, per-context
// metadata, a wide notes textarea, the status-transition controls, and
// the Escalate action. When a judgment plugin is supplied it additionally
// renders the severity-assessment dropdown, the report-attestation
// checkbox, and the Certify / Decline / Escalate trio.

import { CheckCircle2 } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { TrackedItemNotesField } from "./TrackedItemNotesField";
import { TrackedItemStatusChip } from "./TrackedItemStatusChip";
import { escalationExits, resolveStateDef } from "./types";
import { useDateFormatter } from "@/utils/datetime";
import type {
  JudgmentAssessment,
  JudgmentPluginConfig,
  StateSet,
  TrackedItemEscalationStatus,
  TrackedItemState,
} from "./types";

const ASSESSMENTS: JudgmentAssessment[] = ["Clean", "Minor", "Material", "Critical"];

/**
 * State-transition control. Per the primitive contract: a dropdown when
 * more than two next states are available, otherwise a button per state.
 * Shared by the row (compact) and the modal.
 */
export function TrackedItemStateControl({
  state,
  stateSet,
  onStateChange,
  pending = false,
  disabled = false,
}: {
  state: TrackedItemState;
  stateSet: StateSet;
  onStateChange: (next: TrackedItemState) => void;
  pending?: boolean;
  disabled?: boolean;
}) {
  const nextStates = stateSet.transitions[state] ?? [];
  if (nextStates.length === 0) return null;

  if (nextStates.length > 2) {
    return (
      <Select
        className="!w-44"
        value=""
        disabled={disabled || pending}
        aria-label="Advance status"
        onChange={(e) => {
          if (e.target.value) onStateChange(e.target.value);
        }}
      >
        <option value="">Advance status…</option>
        {nextStates.map((value) => (
          <option key={value} value={value}>
            {resolveStateDef(stateSet, value).label}
          </option>
        ))}
      </Select>
    );
  }

  return (
    <div className="flex items-center gap-space-1">
      {nextStates.map((value) => (
        <Button
          key={value}
          variant="secondary"
          size="sm"
          disabled={disabled || pending}
          onClick={() => onStateChange(value)}
        >
          {resolveStateDef(stateSet, value).label}
        </Button>
      ))}
    </div>
  );
}

/**
 * Post-escalation action. Once an executive resolves the escalation, an
 * Escalated item is otherwise frozen (no transitions out of Escalated). When
 * Approved this offers every exit (`escalationExits`), because the server reads
 * no directive and accepts any status (owner ruling, 2026-10-26: the screen
 * offers what the server allows); the directive shows in the tooltip. When
 * Denied it offers the return to rework. Renders nothing while the escalation
 * is still Pending or when the item is not Escalated.
 */
export function TrackedItemEscalationAction({
  state,
  stateSet,
  escalationStatus,
  escalationDirective,
  escalationResolvedAt,
  onStateChange,
  pending = false,
  disabled = false,
}: {
  state: TrackedItemState;
  stateSet: StateSet;
  escalationStatus: TrackedItemEscalationStatus;
  escalationDirective?: string | null;
  escalationResolvedAt?: string | null;
  onStateChange: (next: TrackedItemState) => void;
  pending?: boolean;
  disabled?: boolean;
}) {
  const { date } = useDateFormatter();
  if (state !== "Escalated") return null;

  if (escalationStatus === "Approved") {
    const exits = escalationExits(stateSet);
    if (exits.length === 0) return null;
    const resolvedNote = escalationResolvedAt
      ? ` (resolved ${date(escalationResolvedAt)})`
      : "";
    const directiveNote = escalationDirective?.trim()
      ? `: “${escalationDirective.trim()}”`
      : "";
    return (
      <Select
        className="!w-44"
        value=""
        disabled={disabled || pending}
        aria-label="Resolve per directive"
        title={`Per executive escalation directive${resolvedNote}${directiveNote}`}
        onChange={(e) => {
          if (e.target.value) onStateChange(e.target.value);
        }}
      >
        <option value="">Resolve per directive…</option>
        {exits.map((value) => (
          <option key={value} value={value}>
            {resolveStateDef(stateSet, value).label}
          </option>
        ))}
      </Select>
    );
  }

  if (escalationStatus === "Denied") {
    const target = stateSet.reworkState;
    if (!target) return null;
    return (
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled || pending}
        title="The executive denied this escalation — return the item to the working queue to rework it."
        onClick={() => onStateChange(target)}
      >
        Return to Pending
      </Button>
    );
  }

  // Pending — awaiting executive review; no forward action yet.
  return null;
}

interface TrackedItemModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  code?: string;
  description?: string;
  requiredEvidence?: string;
  escalationPath?: string;
  metadata?: Array<{ label: string; value: string }>;
  detailExtra?: React.ReactNode;
  state: TrackedItemState;
  stateSet: StateSet;
  onStateChange: (next: TrackedItemState) => void;
  stateChangePending?: boolean;
  notes: string;
  onNotesChange: (notes: string) => void;
  notesSavePending?: boolean;
  canEscalate: boolean;
  escalationStatus: TrackedItemEscalationStatus;
  escalationDirective?: string | null;
  escalationResolvedAt?: string | null;
  onEscalateClick: () => void;
  judgmentPlugin?: JudgmentPluginConfig;
  isReadOnly?: boolean;
}

export function TrackedItemModal({
  open,
  onClose,
  title,
  code,
  description,
  requiredEvidence,
  escalationPath,
  metadata,
  detailExtra,
  state,
  stateSet,
  onStateChange,
  stateChangePending = false,
  notes,
  onNotesChange,
  notesSavePending = false,
  canEscalate,
  escalationStatus,
  escalationDirective,
  escalationResolvedAt,
  onEscalateClick,
  judgmentPlugin,
  isReadOnly = false,
}: TrackedItemModalProps) {
  const def = resolveStateDef(stateSet, state);
  const controlsDisabled = isReadOnly || def.isTerminal;

  return (
    <Modal open={open} onClose={onClose} dismissOnBackdropClick={false} title={title} size="lg">
      <div className="flex flex-col gap-space-4">
        <div className="flex flex-wrap items-center gap-space-2">
          {code && (
            <span className="font-display text-label uppercase tracking-label text-gold-shadow">
              {code}
            </span>
          )}
          <TrackedItemStatusChip state={state} stateSet={stateSet} />
        </div>

        {description && (
          <Section label="Description">
            <p className="text-small text-onyx m-0 whitespace-pre-line">{description}</p>
          </Section>
        )}

        {requiredEvidence && (
          <Section label="Required evidence">
            <p className="text-small text-onyx m-0 whitespace-pre-line">{requiredEvidence}</p>
          </Section>
        )}

        {metadata && metadata.length > 0 && (
          <Section label="Details">
            <dl className="grid grid-cols-2 gap-space-2 m-0">
              {metadata.map((m) => (
                <div key={m.label}>
                  <dt className="text-caption uppercase tracking-label text-gray-mid">
                    {m.label}
                  </dt>
                  <dd className="text-small text-onyx m-0">{m.value}</dd>
                </div>
              ))}
            </dl>
          </Section>
        )}

        <Section label="Escalation path">
          <p className="text-small text-onyx m-0">
            {escalationPath ?? "Escalation routes to the configured executive lead."}
          </p>
        </Section>

        {detailExtra}

        {judgmentPlugin && (
          <JudgmentPluginPanel plugin={judgmentPlugin} disabled={controlsDisabled} />
        )}

        <TrackedItemNotesField
          value={notes}
          onSave={onNotesChange}
          pending={notesSavePending}
          disabled={isReadOnly}
          rows={5}
          label="Notes"
        />

        <div className="flex flex-wrap items-center justify-between gap-space-3 border-t border-border-light pt-space-3">
          {judgmentPlugin ? (
            <div className="flex items-center gap-space-2">
              <Button
                variant="primary"
                size="sm"
                disabled={controlsDisabled}
                onClick={judgmentPlugin.onCertify}
              >
                Certify
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={controlsDisabled}
                onClick={judgmentPlugin.onDecline}
              >
                Decline
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-space-2">
              <TrackedItemStateControl
                state={state}
                stateSet={stateSet}
                onStateChange={onStateChange}
                pending={stateChangePending}
                disabled={controlsDisabled}
              />
              <TrackedItemEscalationAction
                state={state}
                stateSet={stateSet}
                escalationStatus={escalationStatus}
                escalationDirective={escalationDirective}
                escalationResolvedAt={escalationResolvedAt}
                onStateChange={onStateChange}
                pending={stateChangePending}
                disabled={isReadOnly}
              />
            </div>
          )}
          {canEscalate && (
            <Button
              variant="secondary"
              size="sm"
              disabled={isReadOnly}
              onClick={onEscalateClick}
            >
              Escalate
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-space-1">
      <div className="font-display text-label uppercase tracking-label text-gold-shadow">
        {label}
      </div>
      {children}
    </div>
  );
}

function JudgmentPluginPanel({
  plugin,
  disabled,
}: {
  plugin: JudgmentPluginConfig;
  disabled: boolean;
}) {
  return (
    <Section label="Analyst judgment">
      <div className="flex flex-col gap-space-3 border border-border-light rounded-md p-space-3">
        <div className="flex flex-col gap-space-1">
          <label className="text-caption uppercase tracking-label text-gray-mid">
            Severity assessment{plugin.assessment.required ? " *" : ""}
          </label>
          <Select
            className="!w-48"
            value={plugin.assessment.value ?? ""}
            disabled={disabled}
            aria-label="Severity assessment"
            onChange={(e) =>
              plugin.assessment.onChange(
                e.target.value ? (e.target.value as JudgmentAssessment) : null,
              )
            }
          >
            <option value="">Not assessed</option>
            {ASSESSMENTS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>
        </div>

        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={plugin.reportUploaded.value}
            disabled={disabled}
            onChange={(e) => plugin.reportUploaded.onChange(e.target.checked)}
          />
          Report uploaded to the Document Repository
          {plugin.reportUploaded.required ? " *" : ""}
          {plugin.reportUploaded.value && plugin.reportUploaded.attestedByDisplayName && (
            <span className="inline-flex items-center gap-space-1 text-caption text-gray-mid">
              <CheckCircle2 size={12} aria-hidden />
              {plugin.reportUploaded.attestedByDisplayName}
            </span>
          )}
        </label>
      </div>
    </Section>
  );
}
